import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useThrottledRenderingUrl } from '../../hooks/useThrottledRenderingUrl';
import RPPError from '../../utils/error';
import { createRequestNumberGenerator } from '../../utils/requestNumberGenerator';
import { getAspectRatio } from '../../utils/aspectRatio';
import { loadImageFromBlob } from '../../utils/loadImage';
import { releaseCanvases } from '../../utils/releaseCanvases';
export function CanvasPreview(_ref) {
  let {
    children,
    user,
    view,
    renderOptions,
    cimDocUrl,
    throttleWaitMs,
    onError,
    onPaint
  } = _ref;
  const canvasRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState();
  const requestNumberGenerator = useRef(createRequestNumberGenerator());
  const throttledRenderingRequest = useThrottledRenderingUrl({
    user,
    view,
    renderOptions,
    containerWidth,
    cimDocUrl,
    throttleWaitMs
  });
  const [status, setStatus] = useState({
    loading: false
  });
  const setRef = newRef => {
    if (newRef === null && canvasRef.current) {
      releaseCanvases([canvasRef.current]);
    }
    canvasRef.current = newRef;
  };
  useEffect(() => {
    var _a;
    if (!canvasRef.current || !((_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.parentElement)) {
      return;
    }
    const resizeObserver = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    resizeObserver.observe(canvasRef.current.parentElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!throttledRenderingRequest || !canvasRef.current) {
      return;
    }
    const currentRequestTracker = requestNumberGenerator.current.next();
    const isRequestStillRelevant = () => requestNumberGenerator.current.lastCompletedRequest() < currentRequestTracker;
    setStatus({
      loading: true
    });
    const drawResponse = async response => {
      if (response.ok) {
        if (!isRequestStillRelevant()) {
          return;
        }
        const blob = await response.clone().blob();
        if (!isRequestStillRelevant()) {
          return;
        }
        const {
          image,
          width,
          height
        } = await loadImageFromBlob(blob);
        if (!isRequestStillRelevant()) {
          return;
        }
        requestNumberGenerator.current.setLastCompletedRequest(currentRequestTracker);
        if (canvasRef.current) {
          canvasRef.current.width = width;
          canvasRef.current.height = height;
          const context = canvasRef.current.getContext('2d');
          context.drawImage(image, 0, 0, width, height);
          onPaint === null || onPaint === void 0 ? void 0 : onPaint();
        }
        setStatus({
          loading: false
        });
      } else {
        if (!isRequestStillRelevant()) {
          return;
        }
        const error = new RPPError("Error rendering URL ".concat(response.url), response.status);
        onError === null || onError === void 0 ? void 0 : onError(error);
        setStatus({
          loading: false,
          error
        });
      }
    };
    const handleError = error => {
      if (!isRequestStillRelevant()) {
        return;
      }
      onError === null || onError === void 0 ? void 0 : onError(error);
      setStatus({
        loading: false,
        error
      });
    };
    fetch(throttledRenderingRequest).then(drawResponse).catch(handleError);
  }, [throttledRenderingRequest, setStatus, requestNumberGenerator, canvasRef, onError, onPaint]);
  const defaultStyle = useMemo(() => {
    return {
      aspectRatio: getAspectRatio(view),
      width: '100%',
      objectFit: 'contain',
      height: '100%'
    };
  }, [view]);
  const Canvas = useCallback(props => React.createElement("canvas", Object.assign({
    ref: setRef,
    style: defaultStyle
  }, props)), [defaultStyle]);
  if (!children) {
    return React.createElement(Canvas, null);
  }
  return React.createElement(React.Fragment, null, children({
    Canvas,
    status,
    defaultStyle
  }));
}